import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAppRouteParams } from "@/AppRoutes";
import { useAppAccount } from "@/components/AppProvider/AppAccountProvider";
import { useAppOrgsAndInvitedOrgs } from "@/components/AppProvider/AppOrgsAndInvitedOrgsProvider";
import { useVersions } from "@/components/InOrganizationProvider/VersionsProvider";
import { OPEN_SIDE_MENU_WIDTH } from "@/const";
import {
  checkIsPublishedByFileUpload,
  getLockOperationMessage,
} from "@/domain/Version";

const STORAGE_KEY_PREFIX = "appSideNav.openState";

type SideNavOpenState = {
  isGlobalOpen: boolean;
  isLocalOpen: boolean;
  globalWidth: number;
  localWidth: number;
  // 全閉に遷移する直前のパネル状態。menu-btn 再表示時の復元に使う。
  lastIsGlobalOpen: boolean;
  lastIsLocalOpen: boolean;
};

const defaultSideNavOpenState = (): SideNavOpenState => ({
  isGlobalOpen: true,
  isLocalOpen: true,
  globalWidth: OPEN_SIDE_MENU_WIDTH,
  localWidth: OPEN_SIDE_MENU_WIDTH,
  lastIsGlobalOpen: true,
  lastIsLocalOpen: true,
});

// 開閉状態は組織ごとに保存する (uid × organizationId)。
const buildSideNavOpenStateStorageKey = (
  uid: string,
  organizationId: string
): string => `${STORAGE_KEY_PREFIX}.${uid}.${organizationId}`;

const loadSideNavOpenState = (
  uid: string,
  organizationId: string
): SideNavOpenState => {
  try {
    const raw = localStorage.getItem(
      buildSideNavOpenStateStorageKey(uid, organizationId)
    );
    if (!raw) return defaultSideNavOpenState();
    const parsed = JSON.parse(raw) as Partial<SideNavOpenState>;
    return { ...defaultSideNavOpenState(), ...parsed };
  } catch {
    return defaultSideNavOpenState();
  }
};

const saveSideNavOpenState = (
  uid: string,
  organizationId: string,
  state: SideNavOpenState
): void => {
  // localStorage はストレージ無効環境（embedded webview 等）や容量超過時に
  // 例外を投げる。永続化は best-effort なので失敗は黙って捨てる。
  try {
    localStorage.setItem(
      buildSideNavOpenStateStorageKey(uid, organizationId),
      JSON.stringify(state)
    );
  } catch {
    // noop
  }
};

type AppSideNavOpenStateContextValue = {
  isGlobalOpen: boolean;
  isLocalOpen: boolean;
  globalWidth: number;
  localWidth: number;
  isFullyClosed: boolean;
  // 現在のURLが開閉操作対象か。false の場合、開閉ボタンは非表示にし
  // 表示は常時 open 扱いになる。
  isToggleable: boolean;
  // AppSideNavLocal が実際に描画されるか(versionId, version 状態等)。
  // AppSideNavLocal 側はこの値を使って描画/非描画を判定する。
  isLocalRenderable: boolean;
  setIsGlobalOpen: (next: boolean) => void;
  setIsLocalOpen: (next: boolean) => void;
  setGlobalWidth: (next: number) => void;
  setLocalWidth: (next: number) => void;
  toggleFromMenuBtn: () => void;
  // バージョンへ遷移する操作(バージョン一覧/タイトルのクリック)から呼ぶ。
  // グローバル/ローカルの両ナビを開く。
  openBothPanels: () => void;
  // 組織を切り替える操作から呼ぶ。遷移先組織(targetOrganizationId)の保存状態に
  // 直接 isGlobalOpen=true を書き込むため、遷移後にその組織でグローバルナビが開く。
  openGlobalForOrg: (targetOrganizationId: string) => void;
};

// NOTE: Provider 不在 (TopNavOnlyLayout のエラー画面等) でも動くよう no-op 既定値。
// isToggleable=false で開閉ボタンは出ず常時 open 扱いになる。
const appSideNavOpenStateContext =
  createContext<AppSideNavOpenStateContextValue>({
    isGlobalOpen: true,
    isLocalOpen: true,
    globalWidth: OPEN_SIDE_MENU_WIDTH,
    localWidth: OPEN_SIDE_MENU_WIDTH,
    isFullyClosed: false,
    isToggleable: false,
    isLocalRenderable: false,
    setIsGlobalOpen: () => {},
    setIsLocalOpen: () => {},
    setGlobalWidth: () => {},
    setLocalWidth: () => {},
    toggleFromMenuBtn: () => {},
    openBothPanels: () => {},
    openGlobalForOrg: () => {},
  });

export const AppSideNavOpenStateProvider: React.FC<{
  children: ReactNode;
  isAdminMode?: boolean;
  isHeaderOnly?: boolean;
  isSettingsSideNav?: boolean;
  isVisibleSideNav?: boolean;
}> = ({
  children,
  isAdminMode = false,
  isHeaderOnly = false,
  isSettingsSideNav = false,
  isVisibleSideNav = false,
}) => {
  const { account } = useAppAccount();
  const { organizationId, versionId } = useAppRouteParams();
  // 開閉操作対象のページ判定:
  //   - version:     versionId が確定しているページ (Local も描画され得る)
  //   - global-only: organizationId のみ確定し versionId なし (Global のみ)。
  //                  ヘッダーのみ表示モードの設定サイドナビもこれに含める。
  //   - fixed:       それ以外 (toggle操作不可、常時 open 扱い)
  // 管理者ビューはサイドナビが管理者メニュー固定なので fixed。
  // ヘッダーのみ表示モードはサイドバーが無いため基本 fixed だが、設定サイドナビを
  // 出すページ (isSettingsSideNav) はサイドナビが存在するため global-only 扱いにする。
  // 組織外ルート (アカウント設定・参加組織一覧) もサイドナビが表示される限り
  // global-only 扱いにして開閉できるようにする (サイドナビ非表示ページは fixed)。
  const navContext = isAdminMode
    ? "fixed"
    : isSettingsSideNav
      ? "global-only"
      : isHeaderOnly
        ? "fixed"
        : versionId
          ? "version"
          : organizationId
            ? "global-only"
            : isVisibleSideNav
              ? "global-only"
              : "fixed";
  const isToggleable = navContext !== "fixed";
  const isGlobalOnly = navContext === "global-only";
  const uid = account?.uid;

  const [state, setState] = useState<SideNavOpenState>(() =>
    defaultSideNavOpenState()
  );

  // AppSideNavLocal が実際に描画されるか。versionId が確定し、versions が
  // ロード済みで、file-upload published / lock 等でないバージョンのときのみ true。
  // (組織外ルートでは VersionsProvider 不在だが useVersions は空配列を返すため
  //  isLoadedVersions=false → false となり、副作用なく安全に評価できる)
  const { versions, isLoadedVersions } = useVersions();
  const { orgServiceType } = useAppOrgsAndInvitedOrgs();
  const version = versions.find((v) => v.id === versionId);
  const isLocalRenderable = useMemo(
    () =>
      !!versionId &&
      isLoadedVersions &&
      !!version &&
      !checkIsPublishedByFileUpload(version) &&
      !getLockOperationMessage(version, orgServiceType),
    [versionId, isLoadedVersions, version, orgServiceType]
  );

  // Local が実際に描画され得るかの集約判定。
  // global-only URL でも、version is file-upload/locked でも false になる。
  const isLocalAvailable = !isGlobalOnly && isLocalRenderable;

  // setter を参照安定化するため、uid / organizationId は ref 経由で参照する。
  // (これらの変化で setter の参照が変わると、子コンポーネントの useEffect が
  // 不要に再実行されて localStorage を上書きしてしまう)
  const uidRef = useRef<string | undefined>(uid);
  const organizationIdRef = useRef<string | undefined>(organizationId);
  useEffect(() => {
    uidRef.current = uid;
    organizationIdRef.current = organizationId;
  }, [uid, organizationId]);

  // uid と organizationId が確定したら、その組織の保存状態を localStorage から復元。
  // 組織を切り替えるたびに対象組織の状態をロードし直す。
  // (組織外ルートでは organizationId がないので何もしない = 固定表示)
  const loadedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!uid || !organizationId) return;
    const key = `${uid}.${organizationId}`;
    if (loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;
    setState(loadSideNavOpenState(uid, organizationId));
  }, [uid, organizationId]);

  const persist = useCallback((next: SideNavOpenState) => {
    const currentUid = uidRef.current;
    const currentOrgId = organizationIdRef.current;
    if (currentUid && currentOrgId)
      saveSideNavOpenState(currentUid, currentOrgId, next);
  }, []);

  const setIsGlobalOpen = useCallback(
    (next: boolean) => {
      setState((prev) => {
        const goingToFullyClosed = !next && !prev.isLocalOpen;
        const updated: SideNavOpenState = {
          ...prev,
          isGlobalOpen: next,
          // 全閉に遷移する瞬間に直前の状態を保存
          lastIsGlobalOpen: goingToFullyClosed
            ? prev.isGlobalOpen
            : prev.lastIsGlobalOpen,
          lastIsLocalOpen: goingToFullyClosed
            ? prev.isLocalOpen
            : prev.lastIsLocalOpen,
        };
        persist(updated);
        return updated;
      });
    },
    [persist]
  );

  const setIsLocalOpen = useCallback(
    (next: boolean) => {
      setState((prev) => {
        const goingToFullyClosed = !next && !prev.isGlobalOpen;
        const updated: SideNavOpenState = {
          ...prev,
          isLocalOpen: next,
          lastIsGlobalOpen: goingToFullyClosed
            ? prev.isGlobalOpen
            : prev.lastIsGlobalOpen,
          lastIsLocalOpen: goingToFullyClosed
            ? prev.isLocalOpen
            : prev.lastIsLocalOpen,
        };
        persist(updated);
        return updated;
      });
    },
    [persist]
  );

  const setGlobalWidth = useCallback(
    (next: number) => {
      setState((prev) => {
        if (prev.globalWidth === next) return prev;
        const updated = { ...prev, globalWidth: next };
        persist(updated);
        return updated;
      });
    },
    [persist]
  );

  const setLocalWidth = useCallback(
    (next: number) => {
      setState((prev) => {
        if (prev.localWidth === next) return prev;
        const updated = { ...prev, localWidth: next };
        persist(updated);
        return updated;
      });
    },
    [persist]
  );

  const toggleFromMenuBtn = useCallback(() => {
    setState((prev) => {
      let updated: SideNavOpenState;
      if (!isLocalAvailable) {
        // Local が描画されないページ (global-only URL もしくは Local が描画不可
        // なバージョン) では Local の状態を判定に含めない。
        // 開く時 Local は前回の保存値(lastIsLocalOpen)を引き継ぐ。
        if (!prev.isGlobalOpen) {
          updated = {
            ...prev,
            isGlobalOpen: true,
            isLocalOpen: prev.lastIsLocalOpen,
          };
        } else {
          updated = {
            ...prev,
            isGlobalOpen: false,
            lastIsGlobalOpen: prev.isGlobalOpen,
            lastIsLocalOpen: prev.isLocalOpen,
          };
        }
      } else {
        const isFullyClosed = !prev.isGlobalOpen && !prev.isLocalOpen;
        if (isFullyClosed) {
          // 復元。両方 false で保存されてしまっていたら両方 true にフォールバック
          const restoreGlobal = prev.lastIsGlobalOpen || !prev.lastIsLocalOpen;
          const restoreLocal = prev.lastIsLocalOpen || !prev.lastIsGlobalOpen;
          updated = {
            ...prev,
            isGlobalOpen: restoreGlobal,
            isLocalOpen: restoreLocal,
          };
        } else {
          // 全閉に遷移、直前の状態を保存
          updated = {
            ...prev,
            isGlobalOpen: false,
            isLocalOpen: false,
            lastIsGlobalOpen: prev.isGlobalOpen,
            lastIsLocalOpen: prev.isLocalOpen,
          };
        }
      }
      persist(updated);
      return updated;
    });
  }, [persist, isLocalAvailable]);

  const openBothPanels = useCallback(() => {
    setState((prev) => {
      if (prev.isGlobalOpen && prev.isLocalOpen) return prev;
      const updated = { ...prev, isGlobalOpen: true, isLocalOpen: true };
      persist(updated);
      return updated;
    });
  }, [persist]);

  // 組織切り替え時に呼ぶ。これから遷移する組織の保存状態へ直接 isGlobalOpen=true を
  // 書き込む。遷移後その組織をロードする際にグローバルナビが開く。
  // (現在表示中の組織の state は変更しない)
  const openGlobalForOrg = useCallback((targetOrganizationId: string) => {
    const currentUid = uidRef.current;
    if (!currentUid || !targetOrganizationId) return;
    const saved = loadSideNavOpenState(currentUid, targetOrganizationId);
    saveSideNavOpenState(currentUid, targetOrganizationId, {
      ...saved,
      isGlobalOpen: true,
    });
  }, []);

  const value = useMemo<AppSideNavOpenStateContextValue>(() => {
    // 開閉操作対象外のページでは、保存状態に関わらず常に表示扱いとする。
    const effectiveIsGlobalOpen = isToggleable ? state.isGlobalOpen : true;
    const effectiveIsLocalOpen = isToggleable ? state.isLocalOpen : true;
    // Local が描画されないページでは、見た目上の「全閉」は Global のみで判定する。
    const isFullyClosed = isLocalAvailable
      ? !effectiveIsGlobalOpen && !effectiveIsLocalOpen
      : !effectiveIsGlobalOpen;
    return {
      isGlobalOpen: effectiveIsGlobalOpen,
      isLocalOpen: effectiveIsLocalOpen,
      globalWidth: state.globalWidth,
      localWidth: state.localWidth,
      isFullyClosed,
      isToggleable,
      isLocalRenderable,
      setIsGlobalOpen,
      setIsLocalOpen,
      setGlobalWidth,
      setLocalWidth,
      toggleFromMenuBtn,
      openBothPanels,
      openGlobalForOrg,
    };
  }, [
    state,
    isToggleable,
    isLocalAvailable,
    isLocalRenderable,
    setIsGlobalOpen,
    setIsLocalOpen,
    setGlobalWidth,
    setLocalWidth,
    toggleFromMenuBtn,
    openBothPanels,
    openGlobalForOrg,
  ]);

  return (
    <appSideNavOpenStateContext.Provider value={value}>
      {children}
    </appSideNavOpenStateContext.Provider>
  );
};

export const useAppSideNavOpenState = (): AppSideNavOpenStateContextValue =>
  useContext(appSideNavOpenStateContext);
